import { Action, Reducer } from "redux";
import { createReducer } from "reducers/reducer-util";
import { tassign } from "modules/common/type-assign.util";

/**
 * Action Types
 */
import {
	ActionBoolean,
	ActionNumber,
	ActionString,
} from "actions/types/common.action-types";

/**
 * Actions
 */
import { ViewActions } from "actions/view.actions";
import { ActionWork } from "actions/types/work.action-types";
import { ViewLoginUserTwoFactorStatusEnum } from "app/valueObjects/app/view-login-user-two-factor-status.enum";
import { ActionTwoFactorStatusEnum } from "app/store/actions/types/view.action-types";

/**
 * ----------------------------------
 * App State interface
 * ----------------------------------
 */
export interface ViewLoginState {
	loginURL: string;
	tokenValidated: boolean;
	tokenErrorMessage: string;
	viewLoginUserTwoFactorStatus: ViewLoginUserTwoFactorStatusEnum;
	twoFACodeExpiry: number;
}

/**
 * ----------------------------------
 * Initial State
 * ----------------------------------
 */
export const ViewLoginInitialState: ViewLoginState = {
	loginURL: undefined,
	tokenValidated: false,
	tokenErrorMessage: undefined,
	viewLoginUserTwoFactorStatus: ViewLoginUserTwoFactorStatusEnum.UNCHECKED,
	twoFACodeExpiry: undefined,
};

/**
 * ----------------------------------
 * View State Reducer
 * ----------------------------------
 */

/**
 * Clear the login view state.
 */
export const loginClear = (state: ViewLoginState, action: ActionWork) => {
	// Reset the session state.
	return tassign(state, ViewLoginInitialState);
};

/**
 * Set the login identifier provider URL.
 */
export const loginURLSet = (state: ViewLoginState, action: ActionString) => {
	//Set the login identifier provider URL.
	return tassign(state, { loginURL: action.string });
};

/**
 * Set the view login user two factor required status.
 *
 * @param state 			The state to set.
 * @param action			The action that called this function.
 *
 * @returns 				Retuns the current state of the reducer.
 */
export const viewLoginUserTwoFactorRequiredSet = (
	state: ViewLoginState,
	action: ActionTwoFactorStatusEnum
) => {
	return tassign(state, {
		viewLoginUserTwoFactorStatus: action.status,
	});
};

/**
 * Set the auth token token.
 */
export const loginTokenTokenValidatedSet = (state: ViewLoginState) => {
	// Set the auth token.
	return tassign(state, { tokenValidated: true });
};

/**
 * Set the login error message.
 *
 * @param state 			The state to apply the change too.
 * @param action 			The action that started the change.
 *
 * @returns 				The new state to use.
 */
export const loginTokenErrorSet = (
	state: ViewLoginState,
	action: ActionString
) => {
	//Set the login error message.
	return tassign(state, { tokenErrorMessage: action.string });
};

/**
 * Set the 2FA code expiry.
 *
 * @param state 			The state to change.
 * @param action 			The action that requested the change.
 *
 * @return					The returned store state.
 */
export const twoFACodeExpirySet = (
	state: ViewLoginState,
	action: ActionNumber
) => {
	// Set the 2FA code expiry.
	return tassign(state, { twoFACodeExpiry: action.number });
};

/**
 * ----------------------------------
 * Reducers Mapping
 * ----------------------------------
 */

/**
 * Reducers handlers object ... match actions to the handler functions
 */
let reducerHandlers = {};

/**
 * Map the actions to the reducer functions this will allow us to
 */
reducerHandlers[ViewActions.LOGIN_CLEAR] = loginClear;
reducerHandlers[ViewActions.LOGIN_URL_SET] = loginURLSet;
reducerHandlers[ViewActions.LOGIN_TOKEN_VALIDATED_SET] =
	loginTokenTokenValidatedSet;
reducerHandlers[ViewActions.USER_TWO_FACTOR_REQUIRED_SET] =
	viewLoginUserTwoFactorRequiredSet;
reducerHandlers[ViewActions.LOGIN_TOKEN_ERROR_SET] = loginTokenErrorSet;
reducerHandlers[ViewActions.TWO_FA_CODE_EXPIRY_SET] = twoFACodeExpirySet;

/**
 * Create a reducers based on the reducers handlers
 */
export const ViewLoginReducer: Reducer<ViewLoginState> = createReducer(
	ViewLoginInitialState,
	reducerHandlers
);

/**
 * Check if this reducers can handel the function specified
 */
export const ViewLoginReducerHasHandler = (actionType: string): boolean =>
	reducerHandlers.hasOwnProperty(actionType);
